/**
 * Flash report — the flash recorder's uplink. The founder does nothing.
 *
 * The flash-trace ring (flash-trace.ts) and the detectors (flash-detect.ts)
 * see every seam and every visible disruption, but only inside his browser.
 * This leaf listens to the ring and, when a disruption lands, waits 1.5 s so
 * the aftermath is in the ring too, then POSTs ONE `screen:flashed` event to
 * the authed telemetry door (POST /api/telemetry → log_events + PostHog).
 *
 * A disruption: a remount, a vanish or a loading screen that his own
 * navigation did not cause, a big layout shift, an automatic reload, a gate
 * verdict other than ready, a gate re-check that a verdict or the curtain
 * follows within 3 s, an error boundary fallback, a skew reload, the curtain
 * rising again after this session saw it fall, a theme flip after the first
 * second, and (production builds only) a second shell mount. The first one in
 * a window names the report; the rest are counted. A window the page was
 * hidden in at any moment is dropped: nobody saw it.
 *
 * Never throws, never blocks: fire-and-forget with keepalive, at most 20 in
 * any 10 minutes, and nothing at all while signed out. A page going away
 * parks its pending report in sessionStorage and the next page sends it.
 * Props are primitives: the pathname alone, element summaries of tag + test
 * id + class, and the ring's last 15 kinds and details with their ms from
 * the trigger.
 */
import { ANALYTICS_EVENTS } from "@bash/shared";
import { authedFetch, getOptionalAccessToken } from "@/lib/api/client";
import { isDesktopShell } from "@/lib/desktop-shell";
import {
  onFlashTrace,
  readFlashTrace,
  type FlashTraceEntry,
} from "@/lib/flash-trace";
import {
  readAttention,
  startFlashDetect,
  type Attention,
} from "@/lib/flash-detect";

/** Where a page going away parks its pending report for the next page. */
export const FLASH_DEFERRED_KEY = "vestra:flash-deferred";

const REPORT_WINDOW_MS = 1500;
const REPORT_CAP = 20;
const REPORT_CAP_WINDOW_MS = 10 * 60_000;
const RING_TAIL = 15;
const RING_MAX_CHARS = 2000;
const DETAIL_MAX = 120;
const DEFERRED_MAX_CHARS = 8000;
/** A DOM change this soon after his navigating input is his own navigation. */
const NAV_GRACE_MS = 2000;
/** A gate re-check is a disruption only if a verdict or the curtain follows this soon. */
const RECHECK_FOLLOW_MS = 3000;
/** Boot work (the theme applied, the first loading screen) is not a disruption. */
const BOOT_MS = 1000;
/** The unload's own hide lands just before pagehide; an older hide was a real one. */
const UNLOAD_HIDE_SLACK_MS = 100;

type Prop = string | number | boolean | null;

interface TelemetryEvent {
  event_id: string;
  event: string;
  ts: number;
  props: Record<string, Prop>;
}

interface Pending {
  entry: FlashTraceEntry;
  attention: Attention;
  sinceLoadMs: number;
  triggers: number;
  /** When the page was first hidden during the window, or null. */
  hiddenAt: number | null;
}

/** When each report of the last 10 minutes went (module state = one page load). */
const sentAt: number[] = [];

function capOpen(now: number): boolean {
  while (sentAt.length > 0 && now - sentAt[0] >= REPORT_CAP_WINDOW_MS)
    sentAt.shift();
  return sentAt.length < REPORT_CAP;
}

/** `[kind, detail, ms from the trigger]` for the last 15, capped at 2000 chars. */
function compactRing(entries: FlashTraceEntry[], anchorAt: number): string {
  const tail = entries
    .slice(-RING_TAIL)
    .map((e) => [
      String(e.kind),
      e.detail === undefined ? null : String(e.detail).slice(0, DETAIL_MAX),
      e.at - anchorAt,
    ]);
  let json = JSON.stringify(tail);
  while (json.length > RING_MAX_CHARS && tail.length > 0) {
    tail.shift();
    json = JSON.stringify(tail);
  }
  return json;
}

function eventId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function buildEvent(p: Pending, deferred: boolean): TelemetryEvent {
  const build = process.env.BUILD_COMMIT_SHA || "unknown";
  return {
    event_id: eventId(),
    event: ANALYTICS_EVENTS.SCREEN_FLASHED,
    ts: Date.now(),
    props: {
      // The detector's facts first (root_removed / root_added, body,
      // shift_source, auto), so no fact can shadow a named prop below.
      ...p.entry.data,
      kind: String(p.entry.kind),
      detail:
        p.entry.detail === undefined
          ? null
          : String(p.entry.detail).slice(0, DETAIL_MAX),
      path: p.entry.path,
      visible: p.attention.visible,
      sinceLoadMs: p.sinceLoadMs,
      sinceVisibleMs: p.attention.sinceVisibleMs,
      sinceFocusMs: p.attention.sinceFocusMs,
      sinceNavInputMs: p.attention.sinceNavInputMs,
      host: isDesktopShell() ? "native" : "web",
      build,
      triggers: p.triggers,
      deferred,
      ring: compactRing(readFlashTrace(), p.entry.at),
    },
  };
}

async function post(makeEvent: () => TelemetryEvent): Promise<void> {
  try {
    if (!capOpen(performance.now())) return;
    const token = await getOptionalAccessToken();
    if (!token || !capOpen(performance.now())) return;
    sentAt.push(performance.now());
    await authedFetch("/api/telemetry", {
      method: "POST",
      body: JSON.stringify({ events: [makeEvent()] }),
      keepalive: true,
    });
  } catch {
    // The door is off (404), down, or refused: the ring still has it locally.
  }
}

/** Send what the last page parked on its way out. Read once, then gone. */
function sendDeferred(): void {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(FLASH_DEFERRED_KEY);
    if (raw !== null) sessionStorage.removeItem(FLASH_DEFERRED_KEY);
  } catch {
    return;
  }
  if (raw === null) return;
  try {
    const event = JSON.parse(raw) as TelemetryEvent;
    if (event?.event === ANALYTICS_EVENTS.SCREEN_FLASHED)
      void post(() => event);
  } catch {
    // A parked report that does not parse is dropped.
  }
}

/** No grace after a return; otherwise a DOM change right after navigating input is his own. */
function isHisNavigation(a: Attention): boolean {
  const returned =
    (a.sinceFocusMs !== null && a.sinceFocusMs < NAV_GRACE_MS) ||
    (a.sinceVisibleMs !== null && a.sinceVisibleMs < NAV_GRACE_MS);
  return (
    !returned && a.sinceNavInputMs !== null && a.sinceNavInputMs < NAV_GRACE_MS
  );
}

/** Listen to the ring and report disruptions. Returns the stop (tests). */
export function startFlashReporter(): () => void {
  const startedAt = performance.now();
  let mounts = 0;
  let curtainFell = false;
  let recheck: {
    entry: FlashTraceEntry;
    attention: Attention;
    at: number;
  } | null = null;
  let pending: Pending | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  type Trigger = { entry: FlashTraceEntry; attention: Attention; at: number };

  /** A verdict or the curtain within 3 s of a re-check makes the re-check the report. */
  const followRecheck = (now: number): Trigger | null => {
    const r = recheck;
    recheck = null;
    return r && now - r.at <= RECHECK_FOLLOW_MS ? r : null;
  };

  const classify = (
    e: FlashTraceEntry,
    attention: Attention,
    now: number
  ): Trigger | null => {
    const self = { entry: e, attention, at: now };
    switch (e.kind) {
      case "remount":
      case "vanish":
        return isHisNavigation(attention) ? null : self;
      case "loading":
        return now - startedAt >= BOOT_MS && !isHisNavigation(attention)
          ? self
          : null;
      case "shift":
      case "boundary_fallback":
      case "skew_reload":
        return self;
      case "gate_recheck":
        recheck = self;
        return null;
      case "gate":
        return followRecheck(now) ?? self;
      case "curtain_down":
        curtainFell = true;
        return followRecheck(now);
      case "curtain_up": {
        const rose = curtainFell;
        curtainFell = false;
        return rose ? self : null;
      }
      case "reload":
        return typeof e.data?.auto === "string" ? self : null;
      case "theme":
        return now - startedAt >= BOOT_MS ? self : null;
      case "mount":
        // Strict Mode runs every effect twice outside production.
        mounts += 1;
        return process.env.NODE_ENV === "production" && mounts >= 2
          ? self
          : null;
      default:
        return null;
    }
  };

  const takePending = (): Pending | null => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const p = pending;
    pending = null;
    return p;
  };

  const flush = () => {
    const p = takePending();
    if (!p || p.hiddenAt !== null || document.visibilityState !== "visible")
      return;
    void post(() => buildEvent(p, false));
  };

  const unsubscribe = onFlashTrace((entry) => {
    const now = performance.now();
    if (pending && pending.hiddenAt === null) {
      if (entry.kind === "visible" && entry.detail !== "visible")
        pending.hiddenAt = now;
    }
    const attention = readAttention(now);
    const trigger = classify(entry, attention, now);
    if (!trigger) return;
    if (pending) {
      pending.triggers += 1;
      return;
    }
    pending = {
      entry: trigger.entry,
      attention: trigger.attention,
      sinceLoadMs: Math.round(trigger.at),
      triggers: 1,
      hiddenAt: trigger.attention.visible ? null : trigger.at,
    };
    timer = setTimeout(flush, REPORT_WINDOW_MS);
  });

  // A page going away cannot wait on the session: park the report for the
  // next page. The unload's own hide does not count against it; a hide from
  // earlier in the window does.
  const onPageHide = () => {
    const p = takePending();
    if (!p) return;
    if (
      p.hiddenAt !== null &&
      performance.now() - p.hiddenAt > UNLOAD_HIDE_SLACK_MS
    )
      return;
    try {
      const json = JSON.stringify(buildEvent(p, true));
      if (json.length <= DEFERRED_MAX_CHARS)
        sessionStorage.setItem(FLASH_DEFERRED_KEY, json);
    } catch {
      // Storage refused: the report is lost, the ring is not.
    }
  };
  window.addEventListener("pagehide", onPageHide);

  sendDeferred();

  return () => {
    unsubscribe();
    window.removeEventListener("pagehide", onPageHide);
    takePending();
  };
}

let started = false;

/** Start the recorder once per page: the reporter first, so it hears the detectors' first entries. */
export function startFlashRecorder(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  try {
    startFlashReporter();
    startFlashDetect();
  } catch {
    // Diagnostics never break the page.
  }
}
